import { SearchResultsList, useDebouncedCallback } from '@mntn/select-components'
import {
  Icon,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Pressable,
  ScrollArea,
  Skeleton,
  Text,
} from '@mntn/ui-components'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useCampaignsService } from '@/flight-deck-api/services/campaigns.service'
import { useAuthenticatedSession } from '@/providers/session'
import { Button } from './button'
import { SELECT_THEME_CLASSNAME } from './mfe-theme-wrapper'

const PAGE_SIZE = 20

export function CreateExperimentDialog({
  onOpenChange,
  onSubmit,
  open,
}: {
  onOpenChange: (open: boolean) => void
  onSubmit: (clonedFromCampaignGroupId: number) => Promise<void>
  open: boolean
}) {
  const { session } = useAuthenticatedSession()
  const { findCampaignGroups } = useCampaignsService()
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [selectedCampaignGroupId, setSelectedCampaignGroupId] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const scrollCampaignsListRef = useRef<HTMLDivElement>(null)

  const sourcesQuery = useInfiniteQuery({
    queryKey: ['experiment-sources', session.advertiser.id, search],
    queryFn: ({ pageParam }) =>
      // biome-ignore lint/plugin: RETIRED, do not copy. Call it through useSelectQuery/useSelectMutation when touching this.
      findCampaignGroups.handler({
        limit: PAGE_SIZE,
        page: pageParam,
        search: search || undefined,
        statuses: ['LIVE'],
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      pages.flatMap(page => page.rows).length < lastPage.count ? pages.length : undefined,
    enabled: open,
  })

  const campaigns = sourcesQuery.data?.pages.flatMap(page => page.rows) ?? []
  const showEmptyState = campaigns.length === 0 && !sourcesQuery.isFetching

  const updateSearch = useDebouncedCallback((value: string) => {
    const viewport = scrollCampaignsListRef.current?.querySelector('[data-radix-scroll-area-viewport]')
    if (viewport) viewport.scrollTop = 0
    setSearch(value.trim())
  }, 400)

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      updateSearch('')
      setInputValue('')
      setSearch('')
      setSelectedCampaignGroupId(null)
      setErrorMessage(undefined)
    }
    onOpenChange(nextOpen)
  }

  const handleSubmit = async () => {
    if (selectedCampaignGroupId === null) return
    setErrorMessage(undefined)
    setIsSubmitting(true)
    try {
      await onSubmit(selectedCampaignGroupId)
      handleOpenChange(false)
    } catch {
      setErrorMessage('There was an issue creating the experiment. Try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent className="bg-background text-header w-full max-w-xl" wrapperClassName={SELECT_THEME_CLASSNAME}>
        <ModalHeader>
          <ModalTitle>Create campaign as experiment</ModalTitle>
          <ModalDescription className="text-secondary">
            Choose an existing live campaign to record as the experiment source.
          </ModalDescription>
        </ModalHeader>

        <Input
          aria-label="Search campaigns"
          placeholder="Search..."
          value={inputValue}
          inputClassName="pl-10"
          iconLeft={<Icon name="Search" className="size-4" />}
          iconRight={
            inputValue ? (
              <Button
                aria-label="Clear search"
                size="tiny"
                variant="bordered"
                className="p-2 hover:bg-background rounded-full"
                onClick={() => {
                  updateSearch('')
                  setInputValue('')
                  setSearch('')
                  setSelectedCampaignGroupId(null)
                }}
              >
                <Icon name="X" className="size-4" />
              </Button>
            ) : null
          }
          onChange={event => {
            setInputValue(event.target.value)
            updateSearch(event.target.value)
            setSelectedCampaignGroupId(null)
          }}
        />

        {sourcesQuery.isError ? (
          <Text className="text-error">There was an issue loading campaigns. Try again.</Text>
        ) : showEmptyState ? (
          <div className="h-[343px] flex flex-col items-center justify-center">
            <Icon name="Search" className="size-16 text-primary-accent mb-4" />
            <Text variant="lg-b" className="text-header pb-4">
              Search for a Campaign
            </Text>
            <Text variant="base-sm" className="text-secondary">
              No live campaigns found.
            </Text>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[1fr_8rem_2rem] gap-2 p-4 border-b border-border w-full">
              <Text variant="h6" className="text-header">
                Campaign Name
              </Text>
              <Text variant="h6" className="text-header">
                Campaign ID
              </Text>
              <div />
            </div>

            <ScrollArea className="h-[286px]" ref={scrollCampaignsListRef}>
              <SearchResultsList
                key={search}
                results={campaigns}
                renderItem={campaign => {
                  const isSelected = selectedCampaignGroupId === campaign.campaignGroupId

                  return (
                    <Pressable
                      onPress={() => setSelectedCampaignGroupId(campaign.campaignGroupId)}
                      className={`h-auto grid grid-cols-[1fr_8rem_2rem] items-center gap-2 px-4 py-[18px] w-full border-b border-border transition-colors text-left text-header ${isSelected ? 'bg-sub-surface-bg' : 'bg-surface-bg hover:bg-sub-surface-bg'}`}
                    >
                      <Text variant="base" className="truncate">
                        {campaign.name}
                      </Text>
                      <Text variant="base">{campaign.campaignGroupId}</Text>
                      {isSelected ? <Icon name="Check" className="size-4 text-primary-accent" /> : <div />}
                    </Pressable>
                  )
                }}
                hasNextPage={sourcesQuery.hasNextPage}
                fetchNextPage={sourcesQuery.fetchNextPage}
                isFetching={sourcesQuery.isFetching}
                isFetchingNextPage={sourcesQuery.isFetchingNextPage}
                status={sourcesQuery.status}
                skeleton={() => <CampaignListSkeleton />}
              />
            </ScrollArea>
          </div>
        )}

        {errorMessage && <Text className="text-error">{errorMessage}</Text>}

        <ModalFooter className="sm:justify-end">
          <Button disabled={selectedCampaignGroupId === null || isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? 'Creating…' : 'Create campaign as experiment'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function CampaignListSkeleton() {
  return (
    <div className="grid grid-cols-[1fr_8rem_2rem] items-center gap-2 px-4 py-[18px] w-full border-b border-border">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-6 w-16" />
      <Skeleton className="size-4" />
    </div>
  )
}
